import { loadStylesheetFromMeta } from '../cold-ui-styles.js';
import componentStyles0 from '../../styles/confirm-dialog-lit.css?inline';
import { isFunction } from '../../shared/runtime-values.js';
import { LitElement, type TemplateResult } from 'lit';
import { html, nothing } from 'lit/html.js';
import { t } from '../i18n.js';
import { getAppOverlayHost } from '../app-overlay-host.js';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  alternativeLabel?: string;
}

type Choice = 'confirm' | 'alternative' | 'cancel';
type PendingChoice = { resolve(value: Choice): void; trigger: HTMLElement | null };
type ChoiceState = { status: 'closed' } | { status: 'preparing'; choice: PendingChoice } | { status: 'open'; choice: PendingChoice };

export class ConfirmDialogElement extends LitElement {
  static init() {
    const el = document.createElement('confirm-dialog-element');
    getAppOverlayHost().appendChild(el);
    // SAFETY: The owned application template defines this element and its concrete DOM type.
    const dialog = el as ConfirmDialogElement;
    return { requestConfirmation: (options: ConfirmationOptions) => dialog.requestConfirmation(options), requestChoice: (options: ConfirmationOptions) => dialog.requestChoice(options) };
  }

  static properties: any = {
    title: { type: String },
    message: { type: String },
    confirmLabel: { type: String },
    cancelLabel: { type: String },
    alternativeLabel: { type: String },
  };

  declare title: string;
  declare message: string;
  declare confirmLabel: string;
  declare cancelLabel: string;
  declare alternativeLabel: string;
  private choiceState: ChoiceState = { status: 'closed' };
  declare private _dialog: HTMLDialogElement | null;
  declare private _acceptBtn: HTMLElement | null;

  createRenderRoot(): ShadowRoot | this {
    return this;
  }

  constructor() {
    super();
    this.title = t('confirm.title');
    this.message = '';
    this.confirmLabel = t('confirm.confirm');
    this.cancelLabel = t('confirm.cancel');
    this.alternativeLabel = '';
  }

  firstUpdated() {
    this._dialog = this.renderRoot.querySelector('dialog');
    this._acceptBtn = this.renderRoot.querySelector('#confirm-accept');
  }

  _onClose() {
    this._settle('cancel');
  }

  _onCancel() {
    this._settle('cancel');
  }

  _onConfirm() {
    this._settle('confirm');
  }

  _onBackdropClick(e: Event) {
    if (e.target === this._dialog) this._settle('cancel');
  }

  _onCancelEvent(e: Event) {
    e.preventDefault();
    this._settle('cancel');
  }

  _onCloseEvent() {
    if (this.choiceState.status !== 'open' || this._dialog?.open) return;
    this._settle('cancel');
  }

  override disconnectedCallback(): void {
    this._settle('cancel');
    super.disconnectedCallback();
  }

  _settle(accepted: Choice) {
    if (this.choiceState.status === 'closed' || (this.choiceState.status === 'preparing' && accepted !== 'cancel')) return;
    const { choice } = this.choiceState;
    this.choiceState = { status: 'closed' };
    if (this._dialog) {
      if (isFunction(this._dialog.close)) this._dialog.close();
      this._dialog.removeAttribute('open');
    }
    choice.trigger?.focus();
    choice.resolve(accepted);
  }

  async requestConfirmation(options: ConfirmationOptions): Promise<boolean> {
    return await this.requestChoice(options) === 'confirm';
  }

  requestChoice({ title, message, confirmLabel = t('confirm.confirm'), cancelLabel = t('confirm.cancel'), alternativeLabel = '' }: ConfirmationOptions): Promise<Choice> {
    this._settle('cancel');
    let choice!: PendingChoice;
    const result = new Promise<Choice>(resolve => {
      const focused = this.ownerDocument.activeElement;
      choice = { resolve, trigger: focused instanceof HTMLElement ? focused : null };
    });
    const preparation: ChoiceState = { status: 'preparing', choice };
    this.choiceState = preparation;
    this.title = title || t('confirm.title');
    this.message = message || '';
    this.confirmLabel = confirmLabel;
    this.cancelLabel = cancelLabel;
    this.alternativeLabel = alternativeLabel;
    void this.prepareChoice(preparation);
    return result;
  }

  private async prepareChoice(preparation: Extract<ChoiceState, { status: 'preparing' }>): Promise<void> {
    try {
      await this.updateComplete;
      if (this.choiceState !== preparation) return;
      await loadStylesheetFromMeta('app-dialog-stylesheet', this.ownerDocument);
      if (this.choiceState !== preparation) return;
      if (!this._dialog || !this.isConnected) { this._settle('cancel'); return; }
      this.choiceState = { status: 'open', choice: preparation.choice };
      if (isFunction(this._dialog.showModal)) this._dialog.showModal();
      else this._dialog.setAttribute('open', '');
      if (this.choiceState.status === 'open' && this.choiceState.choice === preparation.choice) this._acceptBtn?.focus();
    } catch {
      if (this.choiceState.status !== 'closed' && this.choiceState.choice === preparation.choice) this._settle('cancel');
    }
  }

  render(): TemplateResult {
    return html`

      <style>${componentStyles0}</style>
      <dialog aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-message" @click=${this._onBackdropClick} @cancel=${this._onCancelEvent} @close=${this._onCloseEvent}>
        <div class="share-form mchrome:w-max-768:p-[16px] dlg:p-[24px] dlg:flex dlg:flex-col dlg:gap-[10px] confirm:[confirm-dialog-element_&]:p-[24px] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:flex-col confirm:[confirm-dialog-element_&]:gap-[10px] confirm:w-max-640:[confirm-dialog-element_&]:p-[16px]">
          <div class="feedback-dialog-header dlg:flex dlg:justify-between dlg:items-center dlg:mb-[4px] dlg:[&_h2]:text-[18px] dlg:[&_h2]:font-[700] dlg:[&_h2]:[color:var(--text-primary)] dlg:[&_h2]:m-[0] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:justify-between confirm:[confirm-dialog-element_&]:items-center confirm:[confirm-dialog-element_&]:mb-[4px] confirm:[confirm-dialog-element_&_h2]:text-[18px] confirm:[confirm-dialog-element_&_h2]:font-[700] confirm:[confirm-dialog-element_&_h2]:[color:var(--text-primary,_#f1f5f9)] confirm:[confirm-dialog-element_&_h2]:m-[0]">
            <h2 id="confirm-dialog-title">${this.title}</h2>
            <button class="feedback-dialog-close dlg:w-[32px] dlg:h-[32px] dlg:[border:none] dlg:[background:var(--bg-secondary)] dlg:rounded-[8px] dlg:[cursor:pointer] dlg:flex dlg:items-center dlg:justify-center dlg:[color:var(--text-muted)] dlg:[transition:all_0.15s_ease] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[color:var(--text-secondary)] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] confirm:[confirm-dialog-element_&]:w-[32px] confirm:[confirm-dialog-element_&]:h-[32px] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:[background:var(--bg-secondary,_#334155)] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:items-center confirm:[confirm-dialog-element_&]:justify-center confirm:[confirm-dialog-element_&]:[color:var(--text-muted,_#94a3b8)] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:var(--bg-hover,_#475569)] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[color:var(--text-secondary,_#cbd5e1)]" id="confirm-close" title=${t('confirm_dialog.close')} aria-label=${t('confirm_dialog.close')} @click=${this._onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <p id="confirm-dialog-message">${this.message}</p>
          <div class="feedback-actions dlg:flex dlg:justify-end dlg:gap-[8px] dlg:mt-[2px] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:flex-wrap confirm:[confirm-dialog-element_&]:justify-end confirm:[confirm-dialog-element_&]:gap-[8px] confirm:[confirm-dialog-element_&]:mt-[2px]">
            <button class="feedback-btn feedback-btn-secondary dlg:[border:none] dlg:rounded-[8px] dlg:p-[9px_14px] dlg:text-[13px] dlg:font-[600] dlg:[cursor:pointer] dlg:[transition:all_0.15s_ease] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] dlg:[#magic-link-submit:where(&)]:mt-[12px] dlg:[background:var(--bg-secondary)] dlg:[color:var(--text-secondary)] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:p-[9px_14px] confirm:[confirm-dialog-element_&]:text-[13px] confirm:[confirm-dialog-element_&]:font-[600] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[confirm-dialog-element_&]:[background:var(--bg-secondary,_#334155)] confirm:[confirm-dialog-element_&]:[color:var(--text-secondary,_#cbd5e1)] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:var(--bg-hover,_#475569)]" id="confirm-cancel" @click=${this._onCancel}>${this.cancelLabel}</button>
            ${this.alternativeLabel ? html`<button class="feedback-btn feedback-btn-secondary dlg:[border:none] dlg:rounded-[8px] dlg:p-[9px_14px] dlg:text-[13px] dlg:font-[600] dlg:[cursor:pointer] dlg:[transition:all_0.15s_ease] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] dlg:[#magic-link-submit:where(&)]:mt-[12px] dlg:[background:var(--bg-secondary)] dlg:[color:var(--text-secondary)] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:p-[9px_14px] confirm:[confirm-dialog-element_&]:text-[13px] confirm:[confirm-dialog-element_&]:font-[600] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[confirm-dialog-element_&]:[background:var(--bg-secondary,_#334155)] confirm:[confirm-dialog-element_&]:[color:var(--text-secondary,_#cbd5e1)] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:var(--bg-hover,_#475569)]" id="confirm-alternative" @click=${() => this._settle('alternative')}>${this.alternativeLabel}</button>` : nothing}
            <button class="feedback-btn feedback-btn-primary dlg:[border:none] dlg:rounded-[8px] dlg:p-[9px_14px] dlg:text-[13px] dlg:font-[600] dlg:[cursor:pointer] dlg:[transition:all_0.15s_ease] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] dlg:[#magic-link-submit:where(&)]:mt-[12px] dlg:[background:#15803d] dlg:[color:#fff] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:#166534] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:p-[9px_14px] confirm:[confirm-dialog-element_&]:text-[13px] confirm:[confirm-dialog-element_&]:font-[600] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[confirm-dialog-element_&]:[background:#15803d] confirm:[confirm-dialog-element_&]:[color:#fff] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:#166534]" id="confirm-accept" @click=${this._onConfirm}>${this.confirmLabel}</button>
          </div>
        </div>
      </dialog>
    `;
  }
}

customElements.define('confirm-dialog-element', ConfirmDialogElement);
